import { Forbidden } from '@risksmart-app/components/src/errors/errors';
import { useGetOptionalGuidParam } from '@risksmart-app/components/src/routes/routes.utils';
import type { FC } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { ownerContributorAndViewerIds } from 'src/components/form';
import { useI18NSummaryHelpContent } from 'src/components/help-panel/useSummaryHelpContent';
import type { RiskFormDataFields } from 'src/pages/risks/forms/riskSchema';
import { useHasPermissionQuery } from 'src/rbac/useHasPermission';

import Loading from '@/components/loading';
import { useInsertRisk } from '@/hooks/mutations';
import { useGetRiskById } from '@/hooks/queries';
import { riskDetailsUrl } from '@/utils/urls';

import RiskForm from '../../forms/RiskForm';

const RiskCreateTab: FC = () => {
  useI18NSummaryHelpContent('risks.help');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const tierParam = searchParams.get('tier');
  const parentRiskIdParam = useGetOptionalGuidParam('riskId');

  const {
    data: parentRiskData,
    loading: parentRiskLoading,
    error: parentRiskError,
  } = useGetRiskById({
    queryArgs: { riskId: parentRiskIdParam ?? '' },
    shouldSkip: !parentRiskIdParam,
  });
  const parentRisk = parentRiskData?.risk?.[0] ?? null;

  const { hasPermission: canInsertRisk, loading: permissionLoading } =
    useHasPermissionQuery('insert:risk', parentRisk);

  const { insertRisk } = useInsertRisk();

  if (parentRiskIdParam && (parentRiskLoading || permissionLoading)) {
    return <Loading />;
  }

  if (parentRiskIdParam && parentRiskError) {
    throw new Forbidden('Access denied');
  }

  if (parentRiskIdParam && !parentRisk) {
    throw new Forbidden('Parent risk not found');
  }

  if (parentRiskIdParam && !canInsertRisk) {
    throw new Forbidden('Access denied');
  }

  const onSave = async (riskFormData: RiskFormDataFields) => {
    const data = await insertRisk({
      DepartmentTypeIds:
        riskFormData.departments?.map((d) => d.DepartmentTypeId) || [],
      TagTypeIds: riskFormData.tags?.map((t) => t.TagTypeId) || [],
      Tier: riskFormData.Tier,
      Title: riskFormData.Title,
      Description: riskFormData.Description,
      Treatment: riskFormData.Treatment,
      Status: riskFormData.Status,
      CustomAttributeData: riskFormData.CustomAttributeData ?? null,
      ParentRiskId: riskFormData.ParentRiskId ?? null,
      schedule: riskFormData.schedule,
      ...ownerContributorAndViewerIds(riskFormData),
    });
    if (data?.insertChildRisk?.Id) {
      navigate(riskDetailsUrl(data?.insertChildRisk?.Id), { replace: true });
    }
  };

  const onDismiss = (saved?: boolean) => {
    if (!saved) {
      navigate(-1);
    }
  };

  return (
    <RiskForm
      onSave={onSave}
      onDismiss={onDismiss}
      initialTier={
        tierParam ? Number(tierParam) : parentRiskIdParam ? 2 : undefined
      }
      initialParentRiskId={parentRiskIdParam}
      isParentRiskIdLocked={!!parentRiskIdParam}
    />
  );
};

export default RiskCreateTab;
